import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { DateTimePicker } from '@material-ui/pickers';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { time } from '~/utils';
import {
  CurrencyAutocomplete,
  SelectInput,
  SolutionSelect,
  AddFilterAction,
  AutocompleteArrayTextInput,
} from '~/components';
import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { ActionReport, Moment } from '~/types';
import { HeadTransactionFilters } from '~/types/HeadTransaction';
import { useAllReportSystemsAndTypes } from '~/hooks/actionReports';
import { ReportStatusSelectInput } from '~/resources/actionReports/Filters';
import BackgroundTaskExport from '~/components/BackgroundTaskExport';
import { HeadTransactionSearchFilterDto } from '~/api/backgroundTasks';
import { cleanupFormValues } from '~/utils';
import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import cx from 'classnames';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import OperationSubtypeInput from '~/components/OperationSubtypeInput';
import ServiceInput from '~/components/ServiceInput';
import IssueCodeInput from '~/components/IssueCodeInput';
import {
  SelectTransactionType,
  HEAD_TRANSACTIONS_EXCLUDED_OPERATION_TYPES,
} from '~/components/TransactionsHistory/components/SelectTransactionType';
import { SelectTransactionStatus } from '~/components/TransactionsHistory/components/SelectTransactionStatus';
import { SelectSettled } from '~/components/TransactionsHistory/components/SelectSettled';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';

//--------------------------------------------
const resource = 'headTransactions';

interface Props {
  cleanFilters?: boolean;
}

const Filters = (props: Props) => {
  const { cleanFilters = true } = props;
  const classes = useStyles();
  const listFilterClasses = useListFilterStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string, args?: any): string =>
    translate(`resources.headTransactions.list.filters.${key}`, args);

  const filters = useSelector(selectors.getFilters<HeadTransactionFilters>(resource));
  const [localValues, setLocalValues] = useState<HeadTransactionFilters>(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>([]);

  const [isInitialized, setInitialized] = useState<boolean>(false);

  const filtersAreEmpty = isEmpty(filters);
  useEffect(() => {
    if (cleanFilters || filtersAreEmpty) {
      dispatch(cleanupFilters({ resource }));
      dispatch(
        filterChange({
          resource,
          filters: {
            createdFrom: time().subtract(1, 'months'),
            createdTo: time(),
          },
        })
      );
      setAddedFilters([]);
    }
    setInitialized(true);
  }, [dispatch, cleanFilters, filtersAreEmpty]);

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  useEffect(() => {
    const filterKeyToDisplayKey: Record<string, string> = {
      status: 'transactionStatus',
    };
    const activeDisplayKeys = Object.keys(filters)
      .filter((k) => !!(filters as any)[k])
      .map((k) => filterKeyToDisplayKey[k] || k);

    setAddedFilters((prev) => {
      const merged = new Set([...prev, ...activeDisplayKeys]);
      return Array.from(merged);
    });
  }, [filters]);

  const dispatchFilterChange = (name: string, value: string | Moment) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 500), []);
  const debouncedHandleInput = (e: any) => {
    const { name, value } = e.target;

    if (isInitialized) {
      setLocalValues({
        ...localValues,
        [name]: value,
      });
      debouncedDispatchFilterChange(name, value);
    }
  };

  const hideFilter = (filter: string) => {
    let inputName = filter;
    if (filter === 'transactionStatus') {
      inputName = 'status';
    }
    dispatchFilterChange(inputName, '');
    if (filter === 'issueCode') {
      // reset issueResolved when issueCode is removed
      dispatchFilterChange('issueResolved', '');
    }
    setAddedFilters(addedFilters.filter((f) => f !== filter));
  };

  const allFilters = [
    'senderAccountNumber',
    'recipientAccountNumber',
    'operationType',
    'transactionStatus',
    'baseAmountFrom',
    'baseAmountTo',
    'currency',
    'referenceId',
    'payerSolutionId',
    'payeeSolutionId',
    'reportSystem',
    'reportStatus',
    'settled',
    'operationSubtype',
    'serviceId',
    'transactionTags',
    'issueCode',
    'issueResolved',
  ];

  const renderAddedFilter = (key: string) => {
    switch (key) {
      case 'senderAccountNumber':
        return (
          <TextField
            label={t('senderAccountNumber')}
            name='senderAccountNumber'
            value={localValues.senderAccountNumber ?? ''}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'recipientAccountNumber':
        return (
          <TextField
            label={t('recipientAccountNumber')}
            name='recipientAccountNumber'
            value={localValues.recipientAccountNumber ?? ''}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'operationType':
        return (
          <SelectTransactionType
            label={t('operationType')}
            name='operationType'
            value={localValues.operationType || ''}
            onChange={handleSelectInput}
            exclude={HEAD_TRANSACTIONS_EXCLUDED_OPERATION_TYPES}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'transactionStatus':
        return (
          <SelectTransactionStatus
            value={localValues.status || ''}
            onChange={handleSelectInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'baseAmountFrom':
        return (
          <TextField
            label={t('baseAmountFrom')}
            onChange={debouncedHandleInput}
            value={localValues.baseAmountFrom ?? ''}
            name='baseAmountFrom'
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'baseAmountTo':
        return (
          <TextField
            label={t('baseAmountTo')}
            onChange={debouncedHandleInput}
            value={localValues.baseAmountTo ?? ''}
            name='baseAmountTo'
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'currency':
        return (
          <CurrencyAutocomplete
            label={t('currency')}
            onChange={debouncedHandleInput}
            value={localValues.currency}
            name='currency'
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'referenceId':
        return (
          <TextField
            label={t('referenceId')}
            onChange={debouncedHandleInput}
            value={localValues.referenceId ?? ''}
            name='referenceId'
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'payerSolutionId':
        return (
          <SolutionSelect
            label={t('payerSolutionId')}
            onChange={handleSelectInput}
            value={localValues.payerSolutionId ?? ''}
            name='payerSolutionId'
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'payeeSolutionId':
        return (
          <SolutionSelect
            label={t('payeeSolutionId')}
            onChange={handleSelectInput}
            value={localValues.payeeSolutionId ?? ''}
            name='payeeSolutionId'
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'reportSystem':
        return (
          <SelectInput<ActionReport['system'] | undefined>
            label={t('reportSystem')}
            name='reportSystem'
            value={localValues.reportSystem}
            options={getSystemOptions()}
            onChange={handleSystemChange}
            disabled={schemaQuery.isLoading}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'reportStatus':
        return (
          <ReportStatusSelectInput
            name='reportStatus'
            value={localValues.reportStatus}
            label={t('reportStatus')}
            onChange={handleSelectInput}
            disabled={schemaQuery.isLoading || !localValues.reportSystem}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'settled':
        return (
          <SelectSettled
            onChange={handleSelectInput}
            value={localValues.settled === undefined ? '' : '' + localValues.settled}
            label={t('settled')}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'operationSubtype':
        return (
          <OperationSubtypeInput
            label={t('operationSubtype')}
            name='operationSubtype'
            value={localValues.operationSubtype}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            allowEmpty
          />
        );
      case 'serviceId':
        return (
          <ServiceInput
            label={t('paymentService')}
            name='serviceId'
            value={localValues.serviceId ?? ''}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            allowEmpty
          />
        );
      case 'transactionTags':
        return (
          <AutocompleteArrayTextInput
            label={t('transactionTags')}
            source='transactionTags'
            value={localValues.transactionTags}
            regularHelperText={t('tagsHelperText')}
            onChange={(_e: any, value: string[], reason: string) => {
              switch (reason) {
                case 'create-option':
                case 'remove-option':
                  debouncedHandleInput({ target: { name: 'transactionTags', value } });
                  break;
                case 'clear':
                  debouncedHandleInput({ target: { name: 'transactionTags', value: [] } });
                  break;
                default:
                  break;
              }
            }}
            className={cx(
              listFilterClasses.formControl,
              listFilterClasses.formControlAdd,
              listFilterClasses.formControlAddLong
            )}
          />
        );
      case 'issueCode':
        return (
          <IssueCodeInput
            label={t('issueCode')}
            name='issueCode'
            value={localValues.issueCode}
            onChange={handleIssueCodeChange}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'issueResolved':
        return (
          <SelectInput<string | undefined>
            label={t('issueResolved')}
            name='issueResolved'
            value={localValues.issueResolved}
            options={[
              { value: 'Y', text: t('resolved') },
              { value: 'N', text: t('notResolved') },
            ]}
            onChange={handleSelectInput}
            disabled={!localValues.issueCode}
            allowEmpty
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            helperText={!localValues.issueCode ? t('issueResolvedHint') : undefined}
          />
        );
      default:
        return null;
    }
  };

  // Handle specific filters
  const handleDateTimeChange = (data: Moment, inputType: string) => {
    setLocalValues({
      ...localValues,
      [inputType]: data,
    });
    debouncedDispatchFilterChange(inputType, data);
  };
  const schemaQuery = useAllReportSystemsAndTypes();
  const getSystemOptions = () => {
    return (
      schemaQuery.data?.map(({ system }) => ({
        value: system,
        text: system,
      })) || []
    );
  };

  const handleSystemChange = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
      reportStatus: undefined,
    });
    dispatchFilterChange(name, value);
    dispatchFilterChange('reportStatus', '');
  };

  // To send many filters at once
  const debouncedDispatchAllFilters = useCallback(
    debounce((filters) => {
      dispatch(filterChange({ resource, filters }));
    }, 500),
    [dispatch]
  );

  const handleIssueCodeChange = (e: any) => {
    const { name, value } = e.target;
    if (isInitialized) {
      setLocalValues((prev) => {
        const updated = {
          ...prev,
          [name]: value,
          issueResolved: undefined,
        };
        debouncedDispatchAllFilters(updated);
        return updated;
      });
    }
  };

  const handleSelectInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
    });

    dispatchFilterChange(name, value);
  };

  const translateFilter = (key: string) => {
    switch (key) {
      case 'serviceId':
        return t('paymentService');
      default:
        return t(key);
    }
  };

  // Render added filters before the boolean switch
  // to ensure the switch is always at the end of the filter list
  // and to avoid missmathced filter widths
  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (component) {
        return (
          <div className={listFilterClasses.flexContainer} key={filter}>
            {component}
            <IconButton
              aria-label='delete'
              className={listFilterClasses.deleteIcon}
              onClick={() => hideFilter(filter)}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        );
      } else {
        return null;
      }
    });

  return (
    <Fragment>
      {filters && (
        <div className={listFilterClasses.container}>
          <div className={classes.leftSide}>
            <div className={listFilterClasses.inputsGroup}>
              <DateTimePicker
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('from')}
                value={localValues.createdFrom}
                onChange={(date) => handleDateTimeChange(date as Moment, 'createdFrom')}
              />
              <DateTimePicker
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('to')}
                value={localValues.createdTo}
                onChange={(date) => handleDateTimeChange(date as Moment, 'createdTo')}
              />
              {renderAddedFilters()}
            </div>
          </div>
          <div className={classes.rightSide}>
            <AddFilterAction
              filters={allFilters
                .filter((f) => !addedFilters.includes(f))
                .sort()
                .map((filterKey) => ({
                  key: filterKey,
                  label: translateFilter(filterKey),
                }))}
              onFilterSelected={(filter: string) => {
                setAddedFilters(addedFilters.concat([filter]));
              }}
            />
            <BackgroundTaskExport
              type='HEAD_TRANSACTION_EXPORT'
              queryArguments={handleFilters(localValues)}
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};

const handleFilters = (filters: HeadTransactionFilters): HeadTransactionSearchFilterDto => {
  if (!filters) return {};
  const { createdFrom, createdTo, currency, settled, ...rest } = filters;
  const settledValue: boolean | null =
    settled === 'true' ? true : settled === 'false' ? false : null;
  return cleanupFormValues({
    createdFrom: handleDate(createdFrom),
    createdTo: handleDate(createdTo),
    baseAmountCurrency: currency,
    settled: settledValue,
    ...rest,
  });
};

const handleDate = (date?: Moment | null) => {
  if (!date) return undefined;
  return date.toISOString();
};

const useStyles = makeStyles(() => {
  return {
    leftSide: {
      maxWidth: 'calc(100% - 250px)',
    },
    // Can change width to fit export or other buttons on the right side
    rightSide: { minWidth: 250, textAlign: 'right' },
  };
});

export default Filters;
